import { FunctionComponent, useEffect, useRef } from 'react';
import styles from "./UploadNewContent.module.css";
import cn from 'classnames';
import {Card} from 'react-bootstrap';
import CustomButton, { ButtonType } from '../../../../../component/common/CustomButton/CustomButton';
import FormField, { IFieldTheme, IFieldType } from '../../../../../component/common/FormField/FormField';
import FormTextArea from '../../../../../component/common/FormField/FormTextArea';
import FormSelect from '../../../../../component/common/FormField/FormSelect';
import FormCheckbox from '../../../../../component/common/FormField/FormCheckbox';
import ImageUploader from '../../../../../component/common/ImageUploader/ImageUploader';
import { useDispatch, useSelector } from 'react-redux';
import { uploadContent } from '../../../../../redux/slices/listIcon/actions';
import { cancelUpload, resetUploadFormState, setUploadFormState, initialFormState as initialReduxFormState } from '../../../../../redux/slices/listIcon/slice';
import { AppDispatch, RootState } from '../../../../../redux/store';
import { useFormik } from 'formik';
import * as Yup from 'yup';

interface IUploadNewContentProps {
    className?: string;
};
export const categoryOptions = [
    { value: "all", label: "All Categories" },
    { value: "upload", label: "Upload" },
    { value: "invite_friends", label: "Invite Friends" },
    { value: "fishinity_pro_center", label: "Fishinity pro center" },
    { value: "weather", label: "Weather" },
    { value: "checkin", label: "Check in" },
    { value: "checkout", label: "Check out" },
    { value: "locating_app_action", label: "Locating app action" },
    { value: "support", label: "Support" },
    { value: "ai_gamification", label: "Ai gamification" },
    { value: "rewards", label: "Rewards" },
    { value: "ai_group_chat", label: "Ai group chat" },
    { value: "yearly_cycle", label: "Yearly cycle" },
    { value: "utility_compass", label: "Utility compass" },
    { value: "water_body_tooling", label: "Water body tooling" },
    { value: "moon_phase", label: "Moon phase" }, 
];
export const contentTypeOptions= [
    {value: "admin", label: "Admin"},
    {value: "sponsored", label: "Sponsored"},
];

const validationSchema = Yup.object({
    title: Yup.string().trim().required('Title is required'),
    description: Yup.string().trim().required('Description is required'),
    contentType: Yup.string().required('Content type is required'),
    featureCategory: Yup.string().required('Feature category is required'),
    file: Yup.mixed()
        .required('Please upload an image or a video file')
        .test('fileType', 'Only image and video files are allowed', (value) => {
            if (!value) return true;
            return (value as File).type.startsWith('image/') || (value as File).type.startsWith('video/');
        })
        .test('fileSize', 'File size should not exceeds 20GB', (value) => {
            if (!value) return true;
            return (value as File).size <= 20 * 1024 * 1024 * 1024; // 20 GB
        }),
    newsFeed: Yup.boolean(),
    listIcons: Yup.boolean(),
    status: Yup.string(),
});

const UploadNewContent: FunctionComponent<IUploadNewContentProps> = ({className}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { contentUploading, uploadProgress, uploadFormState } = useSelector((state: RootState) => state.listIcon);

    const formik = useFormik({
        initialValues: uploadFormState,
        enableReinitialize: true,
        validationSchema,
        onSubmit: (values) => {
            const data = {
                title: values.title.trim(),
                desc: values.description.trim(),
                content_type: values.contentType,
                category_list_type: values.featureCategory,
                file: values.file,
                upload_to_list: values.listIcons,
                upload_to_news: values.newsFeed,
                status: values.status,
            };
            dispatch(uploadContent(data))
                .unwrap()
                .then(() => {
                    dispatch(resetUploadFormState());
                    formik.resetForm({ values: initialReduxFormState });
                    window.scrollTo(0,0);
                })
                .catch(() => {
                    console.error('Upload content failed');                 
                });
        },
    });
    
    // Create a ref to hold the latest formik values
    const formikValuesRef = useRef(formik.values);
    // Update the ref whenever formik.values changes
    useEffect(() => {
        formikValuesRef.current = formik.values;
    }, [formik.values]);

    // Effect to save state to Redux on unmount
    useEffect(() => {
        return () => {
            dispatch(setUploadFormState(formikValuesRef.current));
        };
    }, [dispatch]);

    const isUploading = contentUploading === 'pending';

    const handleCancel = () => {
        if (isUploading) {
            dispatch(cancelUpload());
        } else {
            // Dispatch to clear Redux state
            dispatch(resetUploadFormState());
            // Explicitly reset formik's internal state to match the now-empty Redux state
            formik.resetForm({ values: initialReduxFormState });
        }
    };

    return (
        <Card className={cn(styles.addNewContentForm, className)}>
            <form onSubmit={formik.handleSubmit}>
                <div className={styles.title}>Upload New Content</div>
                <div className={styles.summary}>Add details about your new content and upload files.</div>
                <div className={cn(styles.addNewContentFormCols)}>
                    <div className={cn(styles.addNewContentFormCol, styles.formCol)}>
                        <FormField 
                            label='Title'
                            fieldType={IFieldType.TEXT} 
                            fieldTheme={IFieldTheme.LIGHT}
                            {...formik.getFieldProps('title')}
                            errorText={formik.errors.title}
                            showError={!!(formik.touched.title && formik.errors.title)}
                        />
                        <FormSelect 
                            label='Content Type'
                            fieldTheme={IFieldTheme.LIGHT}
                            placeholder='Select type'
                            {...formik.getFieldProps('contentType')}
                            errorText={formik.errors.contentType}
                            showError={!!(formik.touched.contentType && formik.errors.contentType)}
                        >
                            <option value="" disabled>Select type</option>
                            {contentTypeOptions.map((option) => 
                                <option key={option.value} value={option.value}>{option.label}</option>
                            )}
                        </FormSelect>
                        <FormSelect 
                            label='Feature Category' 
                            fieldTheme={IFieldTheme.LIGHT}
                            placeholder='Select Category'
                            {...formik.getFieldProps('featureCategory')}
                            errorText={formik.errors.featureCategory}
                            showError={!!(formik.touched.featureCategory && formik.errors.featureCategory)}
                        >
                            {categoryOptions.map((option) => 
                                <option key={option.value} value={option.value} disabled={option.value === "all"}>{option.label}</option>
                            )}
                        </FormSelect>
                        <FormTextArea 
                            label='Description'
                            fieldTheme={IFieldTheme.LIGHT}
                            {...formik.getFieldProps('description')}
                            errorText={formik.errors.description}
                            showError={!!(formik.touched.description && formik.errors.description)}
                        />
                        <div className={styles.checkboxes}>
                            <FormCheckbox 
                                label={'News Feed'} 
                                checked={formik.values.newsFeed} 
                                onChange={() => formik.setFieldValue('newsFeed', !formik.values.newsFeed)} 
                            />
                            <FormCheckbox 
                                label={'List Icons'} 
                                checked={formik.values.listIcons} 
                                onChange={() => formik.setFieldValue('listIcons', !formik.values.listIcons)} 
                            />
                        </div>
                    </div>
                    <div className={cn(styles.addNewContentFormCol, styles.uploadCol)}>
                        <ImageUploader 
                            value={formik.values.file}
                            onChange={(file) => formik.setFieldValue('file', file)} 
                            errorText={formik.errors.file}
                            showError={!!(formik.touched.file && formik.errors.file)}
                            isUploading= {contentUploading=== 'pending'}
                            uploadProgress= {uploadProgress}
                        />
                    </div>
                </div>
                <div className={cn(styles.formOption)}>
                    <CustomButton 
                        className={styles.addNewContentBtn}
                        btnType={ButtonType.LIGHT}
                        onClick={handleCancel}
                        type="button"
                    >
                        {isUploading ? 'Cancel Upload' : 'Cancel'}
                    </CustomButton>
                    <CustomButton 
                        className={styles.addNewContentBtn}
                        btnType={ButtonType.LIGHT}
                        onClick={()=> {
                            formik.setFieldValue('status', 'draft');
                            formik.submitForm();
                        }}
                        disabled={contentUploading === 'pending'}
                        type="button"
                    >
                        {contentUploading === 'pending' && formik.values.status === 'draft' ? `Saving... ${uploadProgress}%` : 'Save As Draft'}
                    </CustomButton>
                    <CustomButton 
                        className={styles.addNewContentBtn}
                        btnType={ButtonType.GREEN}
                        onClick={()=> {
                            formik.setFieldValue('status', 'published');
                            formik.submitForm();
                        }}
                        disabled={isUploading}
                        type="button"
                    >
                        {isUploading && formik.values.status === 'published' ?  `Uploading... ${uploadProgress}%` : 'Upload & Publish'}
                    </CustomButton>
                </div>               
            </form>
        </Card>
    );
};

export default UploadNewContent;
